import type { DecorationWithType, NodeViewProps, NodeViewRenderer } from '@tiptap/core';
import { isiOS, NodeView } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import type {
  Decoration,
  DecorationSource,
  NodeView as ProseMirrorNodeView,
} from '@tiptap/pm/view';
import type { Editor, ReactNodeViewContextProps } from '@tiptap/react';
import { ReactNodeViewContext, ReactRenderer } from '@tiptap/react';
import type { ComponentType } from 'react';
import React from 'react';

import type { ReactNodeViewRendererOptions } from './../../ricos-types';

const isAndroid = () => {
  return navigator.platform === 'Android' || /android/i.test(navigator.userAgent);
};

/*
 * Copied from https://github.com/ueberdosis/tiptap/blob/v2.8.0/packages/react/src/ReactNodeViewRenderer.tsx
 * It's copied because originally Tiptap does not allow to override onDragStart and stopEvent methods
 * This functionality is required to implement drag and drop for the image plugin
 */
export class ReactNodeView<
  Component extends ComponentType<NodeViewProps> = ComponentType<NodeViewProps>,
  NodeEditor extends Editor = Editor,
  Options extends ReactNodeViewRendererOptions = ReactNodeViewRendererOptions
> extends NodeView<Component, NodeEditor, Options> {
  /**
   * The renderer instance.
   */
  renderer!: ReactRenderer<unknown, NodeViewProps>;

  /**
   * The element that holds the rich-text content of the node.
   */
  contentDOMElement!: HTMLElement | null;

  /**
   * Setup the React component.
   * Called on initialization.
   */
  mount() {
    const props = {
      editor: this.editor,
      node: this.node,
      decorations: this.decorations as DecorationWithType[],
      innerDecorations: this.innerDecorations,
      view: this.view,
      selected: false,
      extension: this.extension,
      HTMLAttributes: this.HTMLAttributes,
      getPos: () => this.getPos(),
      updateAttributes: (attributes = {}) => this.updateAttributes(attributes),
      deleteNode: () => this.deleteNode(),
    } satisfies NodeViewProps;

    if (!(this.component as any).displayName) {
      const capitalizeFirstChar = (string: string): string => {
        return string.charAt(0).toUpperCase() + string.substring(1);
      };

      this.component.displayName = capitalizeFirstChar(this.extension.name);
    }

    const onDragStart = this.extension.config.draggable ? this.onDragStart.bind(this) : undefined;
    const nodeViewContentRef: ReactNodeViewContextProps['nodeViewContentRef'] = element => {
      if (element && this.contentDOMElement && element.firstChild !== this.contentDOMElement) {
        element.appendChild(this.contentDOMElement);
      }
    };
    const context = { onDragStart, nodeViewContentRef };
    const Component = this.component;
    // For performance reasons, we memoize the provider component
    // And all of the things it requires are declared outside of the component, so it doesn't need to re-render
    const ReactNodeViewProvider: React.FunctionComponent<NodeViewProps> = React.memo(
      componentProps => {
        return (
          <ReactNodeViewContext.Provider value={context}>
            {React.createElement(Component, componentProps)}
          </ReactNodeViewContext.Provider>
        );
      }
    );

    ReactNodeViewProvider.displayName = 'ReactNodeView';

    if (this.node.isLeaf) {
      this.contentDOMElement = null;
    } else if (this.options.contentDOMElementTag) {
      this.contentDOMElement = document.createElement(this.options.contentDOMElementTag);
    } else {
      this.contentDOMElement = document.createElement(this.node.isInline ? 'span' : 'div');
    }

    if (this.contentDOMElement) {
      // For some reason the whiteSpace prop is not inherited properly in Chrome and Safari
      // With this fix it seems to work fine
      // See: https://github.com/ueberdosis/tiptap/issues/1197
      this.contentDOMElement.style.whiteSpace = 'inherit';
    }

    let as = this.node.isInline ? 'span' : 'div';

    if (this.options.as) {
      as = this.options.as;
    }

    const { className = '' } = this.options;

    this.handleSelectionUpdate = this.handleSelectionUpdate.bind(this);
    this.editor.on('selectionUpdate', this.handleSelectionUpdate);

    this.renderer = new ReactRenderer(ReactNodeViewProvider, {
      editor: this.editor,
      props,
      as,
      className: `node-${this.node.type.name} ${className}`.trim(),
    });
  }

  /**
   * Return the DOM element.
   * This is the element that will be used to display the node view.
   */
  get dom() {
    if (
      this.renderer.element.firstElementChild &&
      !this.renderer.element.firstElementChild?.hasAttribute('data-node-view-wrapper')
    ) {
      throw Error('Please use the NodeViewWrapper component for your node view.');
    }

    return this.renderer.element as HTMLElement;
  }

  /**
   * Return the content DOM element.
   * This is the element that will be used to display the rich-text content of the node.
   */
  get contentDOM() {
    if (this.node.isLeaf) {
      return null;
    }

    return this.contentDOMElement;
  }

  /**
   * On editor selection update, check if the node is selected.
   * If it is, call `selectNode`, otherwise call `deselectNode`.
   */
  handleSelectionUpdate() {
    const { from, to } = this.editor.state.selection;

    const pos = this.getPos();

    if (typeof pos !== 'number') {
      return;
    }

    if (from <= pos && to >= pos + this.node.nodeSize) {
      if (this.renderer.props.selected) {
        return;
      }

      this.selectNode();
    } else {
      if (!this.renderer.props.selected) {
        return;
      }

      this.deselectNode();
    }
  }

  /**
   * On update, update the React component.
   * To prevent unnecessary updates, the `update` option can be used.
   */
  update(
    node: ProseMirrorNode,
    decorations: readonly Decoration[],
    innerDecorations: DecorationSource
  ) {
    const rerenderComponent = (props?: Record<string, any>) => {
      this.renderer.updateProps(props);
    };

    if (node.type !== this.node.type) {
      return false;
    }

    if (typeof this.options.update === 'function') {
      const oldNode = this.node;
      const oldDecorations = this.decorations;
      const oldInnerDecorations = this.innerDecorations;

      this.node = node;
      this.decorations = decorations;
      this.innerDecorations = innerDecorations;

      const updateArgs: Parameters<typeof this.options.update>[0] = {
        oldNode,
        // @ts-expect-error
        oldDecorations,
        newNode: node,
        newDecorations: decorations as DecorationWithType[],
        oldInnerDecorations,
        innerDecorations,
        updateProps: () => rerenderComponent({ node, decorations, innerDecorations }),
      };
      return this.options.update.call(this, updateArgs);
    }

    if (
      node === this.node &&
      this.decorations === decorations &&
      this.innerDecorations === innerDecorations
    ) {
      return true;
    }

    this.node = node;
    this.decorations = decorations;
    this.innerDecorations = innerDecorations;

    rerenderComponent({ node, decorations, innerDecorations });

    return true;
  }

  /**
   * Select the node.
   * Add the `selected` prop and the `ProseMirror-selectednode` class.
   */
  selectNode() {
    this.renderer.updateProps({
      selected: true,
    });
    this.renderer.element.classList.add('ProseMirror-selectednode');

    const { onSelectNode } = this.options;

    if (onSelectNode) {
      const onSelectNodeArgs: Parameters<typeof onSelectNode>[0] = {
        editor: this.editor,
      };
      return onSelectNode.call(this, onSelectNodeArgs);
    }
  }

  /**
   * Deselect the node.
   * Remove the `selected` prop and the `ProseMirror-selectednode` class.
   */
  deselectNode() {
    this.renderer.updateProps({
      selected: false,
    });
    this.renderer.element.classList.remove('ProseMirror-selectednode');
  }

  /**
   * Destroy the React component instance.
   */
  destroy() {
    this.renderer.destroy();
    this.editor.off('selectionUpdate', this.handleSelectionUpdate);
    this.contentDOMElement = null;
  }

  onDragStart(event: React.DragEvent<HTMLElement> | DragEvent) {
    const { view } = this.editor;
    const target = event.target as HTMLElement;
    // get the drag handle element
    // `closest` is not available for text nodes so we may have to use its parent
    const dragHandle =
      target.nodeType === 3
        ? target.parentElement?.closest('[data-drag-handle]')
        : target.closest('[data-drag-handle]');

    const { onDragStart } = this.options;
    if (onDragStart) {
      const onDragStartArgs: Parameters<typeof onDragStart>[0] = {
        editor: this.editor,
        dragHandle,
        target,
        event,
      };
      return onDragStart.call(this, onDragStartArgs);
    }

    if (!this.dom || this.contentDOM?.contains(target) || !dragHandle) {
      return;
    }

    // calculate offset for drag element if we use a different drag handle element
    if (this.dom === dragHandle) {
      event.dataTransfer?.setDragImage(this.dom, 0, 0);
    } else {
      const domBox = this.dom.getBoundingClientRect();
      const handleBox = dragHandle.getBoundingClientRect();
      const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event;

      const x = handleBox.x - domBox.x + nativeEvent.offsetX;
      const y = handleBox.y - domBox.y + nativeEvent.offsetY;
      event.dataTransfer?.setDragImage(this.dom, x, y);
    }

    // we need to tell ProseMirror that we want to move the whole node
    // so we create a NodeSelection
    const selection = NodeSelection.create(view.state.doc, this.getPos());
    const transaction = view.state.tr.setSelection(selection);

    view.dispatch(transaction);
  }

  stopEvent(event: Event): boolean {
    if (!this.dom) {
      return false;
    }

    const { stopEvent } = this.options;
    if (typeof stopEvent === 'function') {
      const stopEventArgs: Parameters<typeof stopEvent>[0] = { event };

      return stopEvent.call(this, stopEventArgs);
    }

    const target = event.target as HTMLElement;
    const isInElement = this.dom.contains(target) && !this.contentDOM?.contains(target);

    // any event from child nodes should be handled by ProseMirror
    if (!isInElement) {
      return false;
    }

    const isDragEvent = event.type.startsWith('drag');
    const isDropEvent = event.type === 'drop';
    const isInput =
      ['INPUT', 'BUTTON', 'SELECT', 'TEXTAREA'].includes(target.tagName) ||
      target.isContentEditable;

    // any input event within node views should be ignored by ProseMirror
    if (isInput && !isDropEvent && !isDragEvent) {
      return true;
    }

    const { isEditable } = this.editor;
    const { isDragging } = this;
    const isDraggable = !!this.node.type.spec.draggable;
    const isSelectable = NodeSelection.isSelectable(this.node);
    const isCopyEvent = event.type === 'copy';
    const isPasteEvent = event.type === 'paste';
    const isCutEvent = event.type === 'cut';
    const isClickEvent = event.type === 'mousedown';

    // ProseMirror tries to drag selectable nodes
    // even if `draggable` is set to `false`
    // this fix prevents that
    if (!isDraggable && isSelectable && isDragEvent) {
      event.preventDefault();
    }

    if (isDraggable && isDragEvent && !isDragging) {
      event.preventDefault();
      return false;
    }

    // we have to store that dragging started
    if (isDraggable && isEditable && !isDragging && isClickEvent) {
      const dragHandle = target.closest('[data-drag-handle]');
      const isValidDragHandle =
        dragHandle && (this.dom === dragHandle || this.dom.contains(dragHandle));

      if (isValidDragHandle) {
        this.isDragging = true;

        document.addEventListener(
          'dragend',
          () => {
            this.isDragging = false;
          },
          { once: true }
        );

        document.addEventListener(
          'drop',
          () => {
            this.isDragging = false;
          },
          { once: true }
        );

        document.addEventListener(
          'mouseup',
          () => {
            this.isDragging = false;
          },
          { once: true }
        );
      }
    }

    // these events are handled by prosemirror
    if (
      isDragging ||
      isDropEvent ||
      isCopyEvent ||
      isPasteEvent ||
      isCutEvent ||
      (isClickEvent && isSelectable)
    ) {
      return false;
    }

    return true;
  }

  ignoreMutation(mutation: MutationRecord | { type: 'selection'; target: Element }) {
    if (!this.dom || !this.contentDOM) {
      return true;
    }

    if (typeof this.options.ignoreMutation === 'function') {
      return this.options.ignoreMutation({ mutation });
    }

    // a leaf/atom node is like a black box for ProseMirror
    // and should be fully handled by the node view
    if (this.node.isLeaf || this.node.isAtom) {
      return true;
    }

    // ProseMirror should handle any selections
    if (mutation.type === 'selection') {
      return false;
    }

    // try to prevent a bug on iOS and Android that will break node views on enter
    // this is because ProseMirror can’t preventDispatch on enter
    // this will lead to a re-render of the node view on enter
    // see: https://github.com/ueberdosis/tiptap/issues/1214
    // see: https://github.com/ueberdosis/tiptap/issues/2534
    if (
      this.dom.contains(mutation.target) &&
      mutation.type === 'childList' &&
      (isiOS() || isAndroid()) &&
      this.editor.isFocused
    ) {
      const changedNodes = [
        ...Array.from(mutation.addedNodes),
        ...Array.from(mutation.removedNodes),
      ] as HTMLElement[];

      // we’ll check if every changed node is contentEditable
      // to make sure it’s probably mutated by ProseMirror
      if (changedNodes.every(node => node.isContentEditable)) {
        return false;
      }
    }

    // we will allow mutation contentDOM with attributes
    // so we can for example adding classes within our node view
    if (this.contentDOM === mutation.target && mutation.type === 'attributes') {
      return true;
    }

    // ProseMirror should handle any changes within contentDOM
    if (this.contentDOM.contains(mutation.target)) {
      return false;
    }

    return true;
  }
}

/**
 * Create a React node view renderer.
 */
export function ReactNodeViewRenderer(
  component: ComponentType<NodeViewProps>,
  options?: Partial<ReactNodeViewRendererOptions>
): NodeViewRenderer {
  return props => {
    // try to get the parent component
    // this is important for vue devtools to show the component hierarchy correctly
    // maybe it’s `undefined` because <editor-content> isn’t rendered yet
    if (!(props.editor as any).contentComponent) {
      return {} as unknown as ProseMirrorNodeView;
    }

    return new ReactNodeView(component, props, options);
  };
}
